/**
    * @description      : 
    * @author           : 
    * @group            : 
    * @created          : 15/09/2025 - 11:46:29
    * 
    * MODIFICATION LOG
    * - Version         : 1.0.0
    * - Date            : 15/09/2025
    * - Author          : 
    * - Modification    : 
**/
import { auth } from '../firebase';

// Base URL for download API endpoints
const API_BASE_URL = 'https://stripe.pv-glarecheck.com/secure-download';

// Response interfaces
interface DownloadUrlResponse {
  success: boolean;
  data?: {
    downloadUrl: string;
    expiresIn: number;
    reportType: string;
  };
  message?: string;
  paymentRequired?: boolean;
}

interface ReportAccessResponse {
  success: boolean;
  data?: {
    hasAccess: boolean;
    freeAccess: boolean;
    paidAccess: boolean;
    excelAccess: boolean;
    paymentRequired: boolean;
    paymentStatus?: boolean;
    message: string;
  };
  message?: string;
}

class DownloadAPI {
  // Cache for URLs to avoid unnecessary API calls
  private urlCache: Map<string, { url: string; expires: number }> = new Map();
  
  /**
   * Get a secure download URL for a report
   * @param reportId The simulation/report ID
   * @param reportType The type of report ('free', 'paid', or 'excel')
   * @returns A promise that resolves to the download URL or null
   */
  async getSecureDownloadUrl(reportId: string, reportType: string): Promise<string | null> {
    try {
      const currentUser = auth.currentUser;
      const uid = currentUser?.uid || 'anon';

      // Cache key includes uid so a different signed-in user can't reuse another's URL
      const cacheKey = `${uid}_${reportId}_${reportType}`;
      const cachedItem = this.urlCache.get(cacheKey);

      if (cachedItem && cachedItem.expires > Date.now()) {
        console.log(`Using cached download URL for ${reportType} report ${reportId}`);
        return cachedItem.url;
      }

      // Get Firebase auth token (auto-refreshes if expired)
      const firebaseToken = await currentUser?.getIdToken();
      if (!firebaseToken) {
        console.error('Authentication required to download reports');
        return null;
      }

      console.log(`Fetching secure download URL for ${reportType} report ${reportId}`);

      const response = await fetch(`${API_BASE_URL}/${reportId}/${reportType}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${firebaseToken}`,
          'Content-Type': 'application/json'
        }
      });

      // Handle different response statuses
      if (response.status === 402) {
        console.log('Payment required for this report');
        // Optionally redirect to payment page
        // window.location.href = `/payment/${reportId}`;
        return null;
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json() as DownloadUrlResponse;

      if (!data.success || !data.data?.downloadUrl) {
        throw new Error(data.message || 'Failed to get download URL');
      }

      // Backend returns expiresIn already in milliseconds. Cache to 90% of that
      // so we refresh slightly before the signed URL actually expires.
      const expirationMs = data.data.expiresIn * 0.9;
      this.urlCache.set(cacheKey, {
        url: data.data.downloadUrl,
        expires: Date.now() + expirationMs
      });

      return data.data.downloadUrl;
    } catch (error) {
      console.error('Error getting secure download URL:', error);
      return null;
    }
  }
  
  /**
   * Check if the user has access to a report
   * @param reportId The simulation/report ID
   * @returns Report access information
   */
  async checkReportAccess(reportId: string): Promise<ReportAccessResponse | null> {
    try {
      // Get Firebase auth token
      const firebaseToken = await auth.currentUser?.getIdToken();
      if (!firebaseToken) {
        console.error('Authentication required to check report access');
        return null;
      }
      
      const response = await fetch(`${API_BASE_URL}/access/${reportId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${firebaseToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }
      
      return await response.json() as ReportAccessResponse;
    } catch (error) {
      console.error('Error checking report access:', error);
      return null;
    }
  }
  
  /**
   * Direct download with redirect (use in new tab or for file downloads)
   * @param reportId The simulation/report ID
   * @param reportType The type of report
   */
  getDirectDownloadUrl(reportId: string, reportType: string): string {
    // This doesn't do an API call - it just constructs the URL for direct download
    // The server will handle authentication and payment checks
    return `${API_BASE_URL}/direct/${reportId}/${reportType}`;
  }
  
  /**
   * Clear URL cache for a specific report or all reports
   * @param reportId Optional report ID to clear specific cache
   */
  clearUrlCache(reportId?: string): void {
    if (reportId) {
      // Cache key is now `${uid}_${reportId}_${reportType}`, so match by `_${reportId}_`
      const keys = Array.from(this.urlCache.keys());
      keys.forEach(key => {
        if (key.includes(`_${reportId}_`)) {
          this.urlCache.delete(key);
        }
      });
      console.log(`Cleared URL cache for report ${reportId}`);
    } else {
      // Clear all cache
      this.urlCache.clear();
      console.log('Cleared all URL cache');
    }
  }
}

// Create singleton instance
export const downloadApi = new DownloadAPI();